import { z } from "zod";
import { createServerFn } from "@tanstack/react-start";
import { getAccessToken } from "@/lib/android/getAndroidProducts";
import { updateSubscription } from "instantdb/server/userServer";
import { instantServerDB } from "instantdb/instantServer";

// Store purchase token mapping for RTDN notifications
async function storePurchaseTokenMapping(
  purchaseToken: string,
  userId: string,
  productId: string,
): Promise<void> {
  await instantServerDB.transact([
    instantServerDB.tx.users[userId].update({
      androidPurchaseToken: purchaseToken,
    }),
  ]);

  console.log(`Storing purchase token mapping: ${purchaseToken} -> ${userId} (${productId})`);
}

interface SubscriptionVerificationResponse {
  acknowledgmentState: number;
  autoRenewing: boolean;
  autoResumeTimeMillis?: string;
  cancelReason?: number;
  countryCode: string;
  developerPayload: string;
  emailAddress?: string;
  expiryTimeMillis: string;
  externalAccountId?: string;
  familyName?: string;
  givenName?: string;
  introductoryPriceInfo?: unknown;
  kind: string;
  linkedPurchaseToken?: string;
  obfuscatedExternalAccountId?: string;
  obfuscatedExternalProfileId?: string;
  orderId: string;
  paymentState?: number;
  priceAmountMicros: string;
  priceChange?: unknown;
  priceCurrencyCode: string;
  profileId?: string;
  profileName?: string;
  promotionCode?: string;
  promotionType?: number;
  purchaseType?: number;
  startTimeMillis: string;
  userCancellationTimeMillis?: string;
}

export type PurchaseVerificationResult = {
  success: boolean;
  isValid: boolean;
  isPurchased: boolean;
  isPending: boolean;
  isAcknowledged: boolean;
  orderId?: string;
  productId?: string;
  error?: string;
  linkedPurchaseToken?: string;
};

const verifyAndroidPurchaseInputSchema = z.object({
  userId: z.string(),
  selectedPlan: z.enum(["monthly", "yearly"]),
  purchaseToken: z.string().min(1, "Purchase token is required"),
  productId: z.string().min(1, "Product ID is required"),
});

export const verifyAndroidPurchase = createServerFn({ method: "POST" })
  .validator(verifyAndroidPurchaseInputSchema)
  .handler(async (ctx): Promise<PurchaseVerificationResult> => {
    try {
      const { userId, selectedPlan, purchaseToken, productId } = ctx.data;

      // Get access token for Google Play API
      const accessToken = await getAccessToken();
      if (!accessToken) {
        return {
          success: false,
          isValid: false,
          isPurchased: false,
          isPending: false,
          isAcknowledged: false,
          error: "Failed to authenticate with Google Play API",
        };
      }

      // Verify the purchase with Google Play
      const verificationResult = await verifySubscriptionPurchase(
        purchaseToken,
        productId,
        accessToken,
      );

      if (!verificationResult.success) {
        return verificationResult;
      }

      // Acknowledge the purchase if it's valid and not already acknowledged
      if (
        verificationResult.isValid &&
        verificationResult.isPurchased &&
        !verificationResult.isAcknowledged
      ) {
        const acknowledgeResult = await acknowledgeSubscription(
          purchaseToken,
          productId,
          accessToken,
        );

        if (!acknowledgeResult) {
          return {
            ...verificationResult,
            error: "Purchase verified but acknowledgment failed",
          };
        }
      }

      // Store purchase token -> userId mapping for RTDN notifications
      await storePurchaseTokenMapping(purchaseToken, userId, productId);

      // Give the user PRO access
      await updateSubscription({
        userId,
        selectedPlan,
        nextBillingDate: new Date(
          selectedPlan === "yearly"
            ? Date.now() + 365 * 24 * 60 * 60 * 1000
            : Date.now() + 30 * 24 * 60 * 60 * 1000,
        ).toISOString(),
        subscriptionProvider: "android",
        status: "active",
      });

      return verificationResult;
    } catch (error) {
      console.error("Purchase verification error:", error);
      return {
        success: false,
        isValid: false,
        isPurchased: false,
        isPending: false,
        isAcknowledged: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  });

async function verifySubscriptionPurchase(
  purchaseToken: string,
  productId: string,
  accessToken: string,
): Promise<PurchaseVerificationResult> {
  try {
    const packageName = process.env.GOOGLE_PLAY_PACKAGE_NAME || "ai.progressmade";

    const response = await fetch(
      `https://androidpublisher.googleapis.com/androidpublisher/v3/applications/${packageName}/purchases/subscriptionsv2/tokens/${purchaseToken}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Subscription verification failed:", response.status, errorText);
      return {
        success: false,
        isValid: false,
        isPurchased: false,
        isPending: false,
        isAcknowledged: false,
        error: `API request failed: ${response.status}`,
      };
    }

    const data: SubscriptionVerificationResponse = await response.json();

    // Check if this is the correct product
    const isCorrectProduct = data.kind === "androidpublisher#subscriptionPurchaseV2";

    // Purchase states: 0 = Purchased, 1 = Canceled, 2 = Pending
    const isPurchased = !data.cancelReason;
    const isPending = data.paymentState === 2;

    // Acknowledgment states: 0 = Yet to be acknowledged, 1 = Acknowledged
    const isAcknowledged = data.acknowledgmentState === 1;

    return {
      success: true,
      isValid: isCorrectProduct && (isPurchased || isPending),
      isPurchased: isPurchased && !isPending,
      isPending,
      isAcknowledged,
      orderId: data.orderId,
      productId,
      linkedPurchaseToken: data.linkedPurchaseToken,
    };
  } catch (error) {
    console.error("Subscription verification error:", error);
    return {
      success: false,
      isValid: false,
      isPurchased: false,
      isPending: false,
      isAcknowledged: false,
      error: error instanceof Error ? error.message : "Verification failed",
    };
  }
}

async function acknowledgeSubscription(
  purchaseToken: string,
  productId: string,
  accessToken: string,
): Promise<boolean> {
  try {
    const packageName = process.env.GOOGLE_PLAY_PACKAGE_NAME || "ai.progressmade";

    const response = await fetch(
      `https://androidpublisher.googleapis.com/androidpublisher/v3/applications/${packageName}/purchases/subscriptions/${productId}/tokens/${purchaseToken}:acknowledge`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Subscription acknowledgment failed:", response.status, errorText);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Subscription acknowledgment error:", error);
    return false;
  }
}
